import {
    CERTIFICATION_WLTP,
    EFFICIENCY_CLASS,
    EFFICIENCY_CLASS_LEXUS,
    EFFICIENCY_CLASS_TMEF,
} from "../../../../../common-deprecated/constants";
import type { BnbSettingsType } from "../../redux/reducers/BnbSettingsReducer";
import type { CarType } from "../../../../shared-logic/types/CarType";
import type { CommonSettingsType } from "../../../../../common-deprecated/settings/fetchCommonSettings";
import {
    isDealer,
    isCod,
    isMasterOrSlave,
    getNextGenerationMatchFromModelMap,
} from "../../../../../common-deprecated/settings/utils/commonSettingUtils";
import type { SortedSpecType } from "../../../../../common-deprecated/types/SpecTypes";
import type { UtilStateType } from "../../../../../common-deprecated/redux/reducers/UtilReducer";
import { ServiceTypeEnum } from "../../../../../common-deprecated/types/SettingsType";
import { getBnbOptionalLabel } from "../labels";
import type { FinanceOptionType } from "../../../../../common-deprecated/types/CommonTypes";
import type { RetailerSettingsType } from "../../../../../shared-logic/features/retailer/redux/RetailerSettingsSlice";

export const loadMassManagement = (bnbSettings: BnbSettingsType): boolean => {
    return !!bnbSettings.labels.massManagementBasketNotification;
};

export const enableExternalPriceCalculation = (
    commonSettings: CommonSettingsType,
    bnbSettings: BnbSettingsType,
    car: CarType,
): boolean => {
    return (
        // Function service URL needs to be defined
        !!bnbSettings.priceCalculationService &&
        // enablePriceServiceCalculation is used for Turkey in order to enable the external service for calculating their prices
        (bnbSettings.enablePriceServiceCalculation ||
            !!commonSettings.query.enablePriceServiceCalculation ||
            // In other cases, this is tied to the enableWLTPPriceCalculation setting which requires the certification to be WLTP as well
            ((bnbSettings.enableWLTPPriceCalculation || bnbSettings.enableWLTPPriceCalculation) &&
                car.certification === CERTIFICATION_WLTP))
    );
};

export const showExternalPriceTax = (bnbSettings: BnbSettingsType): boolean => {
    return !!bnbSettings.showExternalPriceServiceTaxSection;
};

export const showSaveToAccount = (bnbSettings: BnbSettingsType, commonSettings: CommonSettingsType): boolean => {
    return (
        !bnbSettings.hideSaveToYourAccount &&
        !isDealer(commonSettings) &&
        !isCod(commonSettings) &&
        !isMasterOrSlave(commonSettings)
    );
};

export const getEcoImage = (bnbSettings: BnbSettingsType, ecoSpecs?: SortedSpecType | null): string => {
    const energyClass = ecoSpecs?.data?.find(
        (spec) =>
            spec.internalCode === EFFICIENCY_CLASS_TMEF ||
            spec.internalCode === EFFICIENCY_CLASS ||
            spec.internalCode === EFFICIENCY_CLASS_LEXUS,
    );

    return energyClass
        ? bnbSettings.aemEcoLabel?.images?.[energyClass.value.toLowerCase().replace(/\+/g, "Plus")] || ""
        : "";
};

export const shouldHideCarHeaderPrice = (bnbSettings: BnbSettingsType, utils: UtilStateType): boolean => {
    return (
        (bnbSettings.showMonthlyRateDisclaimerInline || bnbSettings.hideCarHeaderMonthlyRate) &&
        utils.financeOption === "monthly"
    );
};

export const fetchIntegratedTradein = (bnbSettings: BnbSettingsType, commonSettings: CommonSettingsType): boolean =>
    !!commonSettings.query.showTradeIn || bnbSettings.enableIntegratedTradeIn;

export const showTradeIn = (
    bnbSettings: BnbSettingsType,
    commonSettings: CommonSettingsType,
    financeOption: FinanceOptionType,
    financeProductName: string | undefined,
): boolean => {
    const isTradeInEnabled = !!commonSettings.query.showTradeIn || bnbSettings.enableIntegratedTradeIn;
    const isAllowedFinanceProduct =
        isAllowedFinanceProductForTradeIn(bnbSettings?.disableTradeInForFinanceProducts, financeProductName) &&
        financeOption === "monthly";
    const isCash = financeOption === "cash";

    return isTradeInEnabled && (isAllowedFinanceProduct || isCash);
};

export const isAllowedFinanceProductForTradeIn = (
    disableTradeInForFinanceProducts: string[] | undefined,
    financeProductName: string | undefined | null,
): boolean => !disableTradeInForFinanceProducts?.includes(financeProductName || "");

export const showServicesStep = (bnbSettings: BnbSettingsType, retailerSettings: RetailerSettingsType): boolean => {
    // If retailerscreen is true we only show the services page when other serviceTypes are available than notSupportedTypes
    if (retailerSettings.isRetailerVariant) {
        const notSupportedTypes = [
            ServiceTypeEnum.Finance,
            ServiceTypeEnum.Financepacks,
            ServiceTypeEnum.Tradein,
            ServiceTypeEnum.Insurance,
        ];
        return !!bnbSettings.services.filter((service) => !notSupportedTypes.includes(service.type)).length;
    }

    return !!bnbSettings.services.length;
};

export const isPassiveSummary = (settings: CommonSettingsType): boolean => !!settings.query.passiveSummary;

export const enableCheckoutSteps = (bnbSettings: BnbSettingsType, retailerSettings: RetailerSettingsType): boolean => {
    return retailerSettings.isRetailerVariant
        ? false
        : bnbSettings.enableBuy || bnbSettings.enableReserve || bnbSettings.enableLeasing;
};

export const getEvDisclaimer = (
    bnbSettings: BnbSettingsType,
    commonSettings: CommonSettingsType,
    engineCode: string,
): string | null => {
    return engineCode.toLowerCase().includes("percombined")
        ? getBnbOptionalLabel({ bnbSettings, commonSettings }, "disclaimerEv")
        : null;
};

export const showNextGenInteriorDisclaimer = (
    bnbSettings: BnbSettingsType,
    commonSettings: CommonSettingsType,
    modelId: string,
): boolean => {
    return (
        !!getBnbOptionalLabel({ commonSettings, bnbSettings }, "nextGenInteriorDisclaimer") &&
        !!getNextGenerationMatchFromModelMap(commonSettings, modelId)
    );
};
